import { useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import PlainButton from 'packages/kakapo-ui-kit/components/buttons/PlainButton';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalFooter from 'packages/kakapo-ui-kit/components/modals/ModalFooter';
import ModalHeader from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Field, { FieldType } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';

import EvaluationSubmissionRequired from './EvaluationSubmissionRequired';
import { CompetencyLevelsInput } from './input/CompetencyInput';

export default function EvaluationSubmissionScoreModal(props: {
	submission: graphql.EvaluationSubmission;
	competencyLevelSet: graphql.CompetencyLevelSet;
	saving: boolean;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();

	const score = props.submission.overrideScore;

	const [submitted, setSubmitted] = useState(false);

	const formik = useFormik({
		initialValues: {
			competency: {
				competency: score
					? props.competencyLevelSet.levels.findIndex((level) => level.id === score.id) ?? undefined
					: undefined,
			},
			reason: props.submission.overrideReason ?? '',
		},
		onSubmit: () => {},
	});
	const [commit, { loading }] = graphql.useOverrideEvaluationSubmissionScoreMutation({});

	const onConfirm = async () => {
		setSubmitted(true);

		if (formik.values.competency.competency === undefined || formik.values.reason.length === 0) {
			return;
		}

		await commit({
			variables: {
				input: {
					id: props.submission.id,
					competencyLevelID:
						props.competencyLevelSet.levels[formik.values.competency.competency!]?.id,
					reason: formik.values.reason || undefined,
				},
			},
		});

		props.onDismiss();
	};

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader>{t('evaluation.submission.overrideScore.modal.title')}</ModalHeader>
				<ModalContent>
					<Paragraph>{t('evaluation.submission.overrideScore.modal.description')}</Paragraph>
					<Box mt={3}>
						<FormikProvider value={formik}>
							<Box mt={3}>
								<Label title={t('evaluation.submission.overrideScore.modal.reason.label')} />
								<Field
									name="reason"
									type={FieldType.Text}
									fullWidth
									placeholder={t('evaluation.submission.overrideScore.modal.reason.placeholder')}
								/>
								{submitted && formik.values.reason.length === 0 && (
									<Box mb={3}>
										<EvaluationSubmissionRequired>
											{t('evaluation.submission.overrideScore.modal.required')}
										</EvaluationSubmissionRequired>
									</Box>
								)}
							</Box>
							<CompetencyLevelsInput
								competency={
									{
										levels: props.competencyLevelSet.levels.map((level) => ({
											enabled: true,
											level,
										})),
									} as graphql.Competency
								}
								name="competency"
								party={graphql.EvaluationSubmissionParty.Manager}
							/>
							{submitted && formik.values.competency.competency === undefined && (
								<Box mt={3}>
									<EvaluationSubmissionRequired>
										{t('evaluation.submission.overrideScore.score.required')}
									</EvaluationSubmissionRequired>
								</Box>
							)}
						</FormikProvider>
					</Box>
				</ModalContent>
				<ModalFooter>
					{props.saving || loading ? (
						<Spinner isLoading size="18px" />
					) : (
						<Flex px={3} alignItems="center" justifyContent="flex-end">
							<Box mr={3}>
								<PlainButton onClick={() => props.onDismiss()}>
									{t('evaluation.submission.overrideScore.modal.cancel')}
								</PlainButton>
							</Box>
							<PrimaryButton onClick={onConfirm}>
								{t('evaluation.submission.overrideScore.modal.confirm')}
							</PrimaryButton>
						</Flex>
					)}
				</ModalFooter>
			</Modal>
		</ModalBackground>
	);
}
